import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Bell,
  CheckCheck,
  Settings,
  ArrowRight,
  AlertTriangle,
  Inbox,
  WifiOff,
  RotateCcw,
} from 'lucide-react';
import { useNotifications } from '../../contexts/NotificationContext';
import { AdminContext } from '../../contexts/AdminContext';
import type { Notification } from '../../types/notification';
import UserNotificationPreferencesModal from '../profile/UserNotificationPreferencesModal';
import NotificationRow from '../notifications/NotificationRow';
import NotificationDetailDialog from '../notifications/NotificationDetailDialog';
import {
  getNotificationCategory,
  getNotificationLink,
  groupByDate,
  isExternalUrl,
} from '../notifications/notificationConfig';

type Tab = 'all' | 'unread';

const DROPDOWN_LIMIT = 8;

const NotificationDropdown: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [tab, setTab] = useState<Tab>('all');
  const [showSettings, setShowSettings] = useState(false);
  const [detailNotification, setDetailNotification] = useState<Notification | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const adminContext = React.useContext(AdminContext);
  const isAdmin = adminContext?.isHostAdmin ?? false;

  const {
    notifications,
    markAsRead,
    markAllAsRead,
    loading,
    hasLoaded,
    error,
    refreshNotifications,
    connectionLost,
  } = useNotifications();

  // Silently refresh when dropdown opens so the user sees the latest.
  // `refreshNotifications` now has a stable identity (NotificationContext hands
  // out memoized callbacks), so depending on it would be safe. The ref is kept
  // as a guard: if that stability ever regresses, depending on the function here
  // would re-fire this effect on every provider render, and since the fetch sets
  // state that is an infinite refetch loop — the bug that left this dropdown
  // stuck on the skeleton ("0 total" but skeleton still showing).
  // Also pass `{ silent: true }` — a non-silent refresh flips `loading` and,
  // when the inbox is empty, replaces the empty state with a skeleton.
  const refreshRef = useRef(refreshNotifications);
  useEffect(() => { refreshRef.current = refreshNotifications; }, [refreshNotifications]);
  useEffect(() => {
    if (isOpen) {
      refreshRef.current({ silent: true }).catch(() => { /* swallow — UI shows error state */ });
    }
  }, [isOpen]);

  // Close on outside click.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]);

  const visibleNotifications = useMemo(
    // System announcements are for every user and must never be filtered out; other
    // 'system'-category notifications stay hidden from non-admins.
    () => (isAdmin
      ? notifications
      : notifications.filter(n => n.source === 'announcement' || getNotificationCategory(n.type) !== 'system')),
    [isAdmin, notifications],
  );
  const unreadCount = visibleNotifications.filter(n => !n.isRead).length;

  const filtered = useMemo(() => {
    const base = tab === 'unread' ? visibleNotifications.filter(n => !n.isRead) : visibleNotifications;
    return base.slice(0, DROPDOWN_LIMIT);
  }, [tab, visibleNotifications]);

  const grouped = useMemo(() => groupByDate(filtered), [filtered]);
  const groupKeys = Object.keys(grouped);

  const activate = useCallback(async (notification: Notification) => {
    if (!notification.isRead) {
      try { await markAsRead(notification.id); } catch { /* optimistic */ }
    }
    const link = getNotificationLink(notification);
    if (link) {
      setIsOpen(false);
      if (isExternalUrl(link.to)) {
        window.open(link.to, '_blank', 'noopener,noreferrer');
      } else {
        navigate(link.to);
      }
    } else {
      setDetailNotification(notification);
    }
  }, [markAsRead, navigate]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen(o => !o)}
        className="relative p-2 text-ink-600 hover:text-ink-900 hover:bg-ink-100 rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-1 -right-1 bg-red-500 text-white text-2xs font-bold px-1.5 rounded-full min-w-[18px] h-[18px] flex items-center justify-center tabular-nums"
            aria-hidden="true"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          role="dialog"
          aria-label="Notifications"
          className="absolute right-0 mt-2 w-[min(calc(100vw-1rem),420px)] bg-surface rounded-xl shadow-2xl border border-ink-200/80 z-50 overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-ink-100">
            <div className="flex items-center gap-2">
              <h2 className="text-15 font-semibold text-ink-900">Notifications</h2>
              {unreadCount > 0 && (
                <span className="bg-red-500 text-white text-11 font-semibold px-1.5 py-0.5 rounded-full tabular-nums">
                  {unreadCount}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => refreshNotifications()}
                className="p-1.5 text-ink-400 hover:text-ink-700 hover:bg-ink-100 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
                aria-label="Refresh notifications"
                title="Refresh"
              >
                <RotateCcw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                type="button"
                onClick={() => { setIsOpen(false); setShowSettings(true); }}
                className="p-1.5 text-ink-400 hover:text-ink-700 hover:bg-ink-100 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
                aria-label="Notification settings"
                title="Notification settings"
              >
                <Settings className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 px-4 pb-2 border-b border-ink-100" role="tablist" aria-label="Filter notifications">
            {([
              { id: 'all' as Tab, label: 'All', count: visibleNotifications.length },
              { id: 'unread' as Tab, label: 'Unread', count: unreadCount },
            ]).map(t => {
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setTab(t.id)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[12.5px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 ${
                    active
                      ? 'bg-ink-900 text-white'
                      : 'text-ink-600 hover:bg-ink-100'
                  }`}
                >
                  {t.label}
                  {t.count > 0 && (
                    <span className={`tabular-nums text-[10.5px] font-semibold ${active ? 'text-white/80' : 'text-ink-400'}`}>
                      {t.count}
                    </span>
                  )}
                </button>
              );
            })}
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => markAllAsRead()}
                className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-ink-600 hover:text-ink-900 px-2 py-1 rounded-md hover:bg-ink-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
                title="Mark all as read"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Mark all read</span>
              </button>
            )}
          </div>

          {/* Connection banner */}
          {connectionLost && (
            <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border-b border-amber-100 text-xs text-amber-800">
              <WifiOff className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="flex-1">You're offline. Notifications may be out of date.</span>
              <button
                type="button"
                onClick={() => refreshNotifications()}
                className="text-xs font-medium text-amber-900 underline underline-offset-2 hover:text-amber-950"
              >
                Retry
              </button>
            </div>
          )}

          {/* Body */}
          <div className="max-h-[460px] overflow-y-auto overscroll-contain">
            {/*
              Only show the skeleton before the first fetch settles. Gating on
              `loading && visibleNotifications.length === 0` flashed a skeleton
              over the empty state whenever a refresh flipped `loading` (dropdown
              open, inbox Refresh, SignalR) even though we already knew the inbox
              was empty. `hasLoaded` keeps the empty state stable after that.
            */}
            {!hasLoaded ? (
              <DropdownSkeleton />
            ) : error && visibleNotifications.length === 0 ? (
              <DropdownError onRetry={refreshNotifications} />
            ) : filtered.length === 0 ? (
              <DropdownEmpty tab={tab} />
            ) : (
              <div className="divide-y divide-ink-100">
                {groupKeys.map(dateGroup => (
                  <div key={dateGroup}>
                    <div className="sticky top-0 z-[1] bg-gradient-to-b from-white to-white/95 backdrop-blur px-4 pt-2.5 pb-1.5 border-b border-ink-100">
                      <h3 className="text-[10.5px] font-semibold uppercase tracking-wider text-ink-500 flex items-center justify-between">
                        <span>{dateGroup}</span>
                        <span className="text-ink-400 font-normal normal-case tabular-nums">
                          {grouped[dateGroup].length}
                        </span>
                      </h3>
                    </div>
                    <div className="divide-y divide-ink-50">
                      {grouped[dateGroup].map(n => (
                        <NotificationRow
                          key={n.id}
                          notification={n}
                          density="compact"
                          onActivate={activate}
                          onMarkRead={(nn) => markAsRead(nn.id)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-t border-ink-100 bg-ink-50/50">
            <span className="text-[11.5px] text-ink-400">
              {visibleNotifications.length > DROPDOWN_LIMIT
                ? `Showing latest ${DROPDOWN_LIMIT}`
                : `${visibleNotifications.length} total`}
            </span>
            <Link
              to="/notifications"
              onClick={() => setIsOpen(false)}
              className="inline-flex items-center gap-1 text-[12.5px] font-medium text-blue-600 hover:text-blue-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 rounded"
            >
              Open inbox
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      )}

      <UserNotificationPreferencesModal isOpen={showSettings} onClose={() => setShowSettings(false)} />
      <NotificationDetailDialog
        notification={detailNotification}
        onClose={() => setDetailNotification(null)}
        onMarkRead={(n) => markAsRead(n.id)}
      />
    </div>
  );
};

const DropdownSkeleton: React.FC = () => (
  <div className="divide-y divide-ink-50">
    {Array.from({ length: 4 }).map((_, i) => (
      <div key={i} className="px-4 py-3 animate-pulse">
        <div className="flex gap-3">
          <div className="h-9 w-9 rounded-lg bg-ink-100" />
          <div className="flex-1 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="h-3 w-44 bg-ink-100 rounded" />
              <div className="h-3 w-10 bg-ink-100 rounded" />
            </div>
            <div className="h-3 w-full bg-ink-100 rounded" />
            <div className="h-3 w-2/3 bg-ink-100 rounded" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

const DropdownError: React.FC<{ onRetry: () => void }> = ({ onRetry }) => (
  <div className="px-6 py-10 text-center">
    <div className="h-10 w-10 rounded-full bg-rose-50 mx-auto mb-3 flex items-center justify-center">
      <AlertTriangle className="h-5 w-5 text-rose-500" />
    </div>
    <p className="text-13 font-semibold text-ink-900">Couldn't load notifications</p>
    <p className="text-xs text-ink-500 mt-1">Check your connection and try again.</p>
    <button
      type="button"
      onClick={onRetry}
      className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12.5px] font-medium text-white bg-ink-900 hover:bg-ink-800 transition-colors"
    >
      <RotateCcw className="h-3.5 w-3.5" />
      Try again
    </button>
  </div>
);

const DropdownEmpty: React.FC<{ tab: Tab }> = ({ tab }) => (
  <div className="px-6 py-10 text-center">
    <div className="h-12 w-12 rounded-2xl bg-ink-50 mx-auto mb-3 flex items-center justify-center">
      <Inbox className="h-6 w-6 text-ink-300" />
    </div>
    <p className="text-13 font-semibold text-ink-900">
      {tab === 'unread' ? "You're all caught up" : 'No notifications yet'}
    </p>
    <p className="text-xs text-ink-500 mt-1 max-w-[280px] mx-auto leading-relaxed">
      {tab === 'unread'
        ? 'Every notification has been read. Nice work!'
        : "When new bookings or requests come in, you'll see them here."}
    </p>
  </div>
);

export default NotificationDropdown;
